import { useEffect, useState } from 'react'
import { Button } from 'components/Button'
import { Footer } from 'components/Footer'
import { Header } from 'components/Header'
import { Layout } from 'components/Layout'
import { Link } from 'components/Link'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { useFetchBlogPosts } from 'features/Blog/api/queries/useFetchBlogPosts'
import { useFetchBlogTags } from 'features/Blog/api/queries/useFetchBlogTags'
import { PostCard } from 'features/Blog/components/PostCard'
import { Tags } from 'features/Blog/components/Tags'
import { Loader } from 'features/components/Loader'
import { useTranslation } from 'hooks/useTranslation'
import { BlogHero } from './components/BlogHero'
import { PostRoot } from '../Post/Post.styles'
import { LoadMore, PostsListRoot, Wrapper } from './PostsList.styles'
import { Search } from 'components/Search'
import { searchItemProp } from 'components/Search/Search'
import { getBlogsMeta, postMetaType } from 'utils/getBlogsMeta'
import { useRouter } from 'next/router'

export const mapVariantsToIndex = ['white', 'neutral', 'secondary-dark', 'secondary-light'] as const

export const PostsList = () => {
  const { t } = useTranslation()
  const [tags, setTags] = useState<string[]>([])
  const router = useRouter()
  const [postEntries, setPostEntries] = useState<searchItemProp[]>([])

  type metaPosts = postMetaType[] | { notFound: boolean }

  const {
    data: posts,
    status: postsStatus,
    fetchNextPage,
    hasNextPage,
  } = useFetchBlogPosts(tags, { keepPreviousData: true })
  const { data: blogTags, status: blogTagsStatus } = useFetchBlogTags()

  useEffect(() => {
    ;(async () => {
      try {
        const posts: metaPosts = await getBlogsMeta()
        if (Array.isArray(posts)) {
          setPostEntries(
            posts.map((post: postMetaType) => ({ label: post.postTitle, value: post.postUrl }))
          )
        }
      } catch (err) {
        console.error(err)
      }
    })()
  }, [])

  if (postsStatus === 'loading' || postsStatus === 'error' || postsStatus === 'idle')
    return <Loader />
  if (blogTagsStatus === 'loading' || blogTagsStatus === 'error' || blogTagsStatus === 'idle')
    return <Loader />

  const aPosts = posts.pages.flatMap((page) => page.items)

  const CTA = (
    <Link href="/meal-plan" passHref>
      <Button as="a" className="header-cta" size="small">
        {t('blog:cta')}
      </Button>
    </Link>
  )

  const header = <Header snapToTop customCTA={CTA} />

  const handleSelect = (item: searchItemProp) => {
    router.push(`/blog/${item.value}`)
  }

  return (
    <PostRoot>
      <Layout header={header} CTA={{ text: 'blog:cta', url: '/' }}>
        <BlogHero />
        <Wrapper>
          <Stack direction="column" gap="7" css={{ maxWidth: '$max', margin: 'auto' }}>
            <Tags tags={blogTags.items} onChange={setTags} activeTags={tags} />
            <Search
              onSelect={handleSelect}
              placeholder="Search for topics here"
              items={postEntries}
              width="600px"
            />
            <div>
              <PostsListRoot>
                {aPosts.map((post, index) => (
                  <PostCard key={post.sys.id} post={post} variant={mapVariantsToIndex[index % 4]} />
                ))}
              </PostsListRoot>

              {hasNextPage && (
                <LoadMore id="load-more-blogs" onClick={() => fetchNextPage()}>
                  <Text size="h4" align="center" weight="bold" color="primary-500">
                    Load more
                  </Text>
                </LoadMore>
              )}
            </div>
          </Stack>
        </Wrapper>
        <Footer />
      </Layout>
    </PostRoot>
  )
}
